import React, { ReactElement, useEffect, useState, useRef } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useForm } from '@tanstack/react-form'
import { groupBy, sortBy } from 'ramda'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  archiveEvent,
  clearEventLifecycleOperationStatus,
  deletePlanningEvent,
  fetchEventCompliances,
  fetchEventDetails,
  fetchEvents,
  fetchPlanningEventsProgress,
  preArchiveEvent,
  reactivateEvent,
  restoreEvent,
  selectEventCompliances,
  selectEventComplianceStatus,
  selectEventDetailsByID,
  selectEventDetailsStatusByID,
  selectEventLifecycleOperationError,
  selectEventLifecycleOperationStatus,
  selectEventLifecycleOperationSuccess,
  selectEvents,
  selectEventsStatus,
  selectPlanningEventProgress,
  selectPlanningEventProgressStatus,
  fetchAllEventInstructors,
  selectInstructors,
  selectAllEventInstructorsStatus,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import {
  fetchFOAMfratInstructors,
  selectFOAMfratInstructors,
  selectFOAMfratInstructorsStatus,
} from 'src/v3/content-pipeline/instructor.slice'
import { selectRoles } from 'src/v3/settings/settings.slice'
import ConfirmAction from 'src/v3/components/confirm-action'
import axios from 'src/utilities/http-client'
import theme from 'src/theme'
import { modalPaperProps } from 'src/v3/utilities/modal-styles'
import { ContentComplianceStatus, EventCompliance } from 'src/v3/core/types'
import { ANCCApproval, CAPCEApproval, QuizApproval } from './field-approval'
import EventField from '../planning/fields/event-field'
import SyllabusFieldEditable from '../planning/fields/syllabus-field-editable'
import CAPCEFieldEditable from '../planning/fields/capce-field-editable'
import ANCCFieldEditable from '../planning/fields/ancc-field-editable'
import TemplateManagement from '../planning/template-management'
import InstructorManagement from '../planning/instructor-management'
import SelectItemHint from '../planning/select-item-hint'
import LifecycleChip from '../components/lifecycle/lifecycle-chip'
import {
  LifecycleFilterValue,
  filterByLifecycle,
  lifecycleCounts,
} from '../components/lifecycle/lifecycle-filter'
import PipelineFilterBar from '../components/pipeline-filter-bar'

type FieldFilter = 'all' | 'pending' | 'approved'

let capitalize = (str: string, noSpace?: boolean) => {
  let separated = !noSpace ? str.replace(/([A-Z])/g, ' $1') : str
  return separated.charAt(0).toUpperCase() + separated.slice(1)
}

let colors: Record<ContentComplianceStatus, any> = {
  new: 'primary',
  approved: 'success',
  rejected: 'error',
  updated: 'primary',
  unapproved: 'error',
}

let FieldApproval = ({
  eventID,
  name,
  status,
  children,
  filter,
  onEdit,
}: {
  eventID: string
  name: keyof Omit<EventCompliance, 'eventID'>
  status: ContentComplianceStatus
  children: ReactElement
  filter: FieldFilter
  onEdit?: () => void
}): ReactElement => {
  let dispatch = useAppDispatch()

  let [loading, setLoading] = useState<'approve' | 'reject'>()

  let remove =
    (filter === 'approved' && status !== 'approved') ||
    (filter === 'pending' && status === 'approved')

  let form = useForm({
    defaultValues: {
      feedback: '',
    },
    // validators: { onChange: approvalSchema },
    onSubmitMeta: {} as { status?: ContentComplianceStatus },
    onSubmit: async ({ value: vals, meta }) => {
      setLoading(meta.status === 'approved' ? 'approve' : 'reject')
      axios
        .post('/api/pipeline/compliance-event', {
          eventID,
          field: capitalize(name, true),
          status: capitalize(meta.status || ''),
          complianceFeedback: vals.feedback,
        })
        .then(() => dispatch(fetchEventCompliances()))
        .finally(() => setLoading(undefined))
    },
  })

  useEffect(() => {
    form.reset()
  }, [eventID])

  return remove ? (
    <></>
  ) : (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'medium',
            }}
          >
            {name === 'ancc' || name === 'capce'
              ? `${name.toUpperCase()} Accreditation`
              : capitalize(name)}
          </Typography>
          {onEdit && (
            <Button
              size="small"
              variant="outlined"
              onClick={onEdit}
              sx={{ minWidth: 'auto', px: 1, py: 0.25 }}
              aria-label={`Edit ${name === 'ancc' || name === 'capce' ? `${name.toUpperCase()} Accreditation` : capitalize(name)} field`}
            >
              Edit
            </Button>
          )}
        </Box>
        <Chip
          size="small"
          label={status === 'new' ? 'Pending Review' : capitalize(status)}
          variant="outlined"
          color={colors[status]}
        />
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: 'background.paperDark',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <form.Field
          name="feedback"
          children={(field) => (
            <TextField
              sx={{ mt: 1.5, mb: 1.25, bgcolor: 'background.paperDark' }}
              fullWidth
              size="small"
              multiline
              label="Feedback"
              name={field.name}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
            />
          )}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {status !== 'approved' && (
            <Button
              color="success"
              type="submit"
              variant="contained"
              onClick={() => form.handleSubmit({ status: 'approved' })}
              disabled={loading === 'approve'}
            >
              {loading === 'approve' ? 'Approving...' : 'Approve'}
            </Button>
          )}
          {status === 'approved' && (
            <Button
              color="error"
              type="submit"
              variant="outlined"
              onClick={() => form.handleSubmit({ status: 'unapproved' })}
              disabled={loading === 'reject'}
            >
              {loading === 'reject' ? 'Unapproving...' : 'Unapprove'}
            </Button>
          )}
          {status !== 'rejected' && status !== 'approved' && (
            <Button
              color="error"
              type="submit"
              variant="outlined"
              onClick={() => form.handleSubmit({ status: 'rejected' })}
              disabled={loading === 'reject'}
            >
              {loading === 'reject' ? 'Rejecting...' : 'Reject'}
            </Button>
          )}
        </Box>
      </form>
    </Card>
  )
}

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let elementRef = useRef(null)

  let [activeEvent, setActiveEvent] = useState<string>()
  let [eventFilter, setEventFilter] = useState<
    'all' | 'needs-revision' | 'in-planning' | 'ready-for-review' | 'approved'
  >('needs-revision')
  let [lifecycleFilter, setLifecycleFilter] =
    useState<LifecycleFilterValue>('all')
  let [instructorFilter, setInstructorFilter] = useState<string | null>(null)
  let [detailsFilter, setDetailsFilter] = useState<FieldFilter>('pending')
  let [searchQuery, setSearchQuery] = useState('')
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [activeTab, setActiveTab] = useState(0)
  let [editModalOpen, setEditModalOpen] = useState(false)
  let [editModalField, setEditModalField] = useState<{
    type:
      | 'event'
      | 'syllabus'
      | 'capce-full'
      | 'ancc-full'
      | 'template'
      | 'instructors'
    fieldName?: string
    fieldLabel: string
  } | null>(null)

  let events = useAppSelector(selectEvents)
  let eventsStatus = useAppSelector(selectEventsStatus)
  let roles = useAppSelector(selectRoles)
  let canTransitionLifecycle = roles.includes('content-compliance')
  let lifecycleOperationStatus = useAppSelector(
    selectEventLifecycleOperationStatus,
  )
  let lifecycleOperationError = useAppSelector(
    selectEventLifecycleOperationError,
  )
  let lifecycleOperationSuccess = useAppSelector(
    selectEventLifecycleOperationSuccess,
  )
  let [lifecycleConfirm, setLifecycleConfirm] = useState<string>('')
  let eventDetails = useAppSelector(selectEventDetailsByID(activeEvent || ''))
  let eventDetailsStatus = useAppSelector(
    selectEventDetailsStatusByID(activeEvent || ''),
  )
  let eventCompliances = useAppSelector(selectEventCompliances)
  let eventComplianceStatus = useAppSelector(selectEventComplianceStatus)
  let planningEventProgress = useAppSelector(selectPlanningEventProgress)
  let planningEventProgressStatus = useAppSelector(
    selectPlanningEventProgressStatus,
  )
  let allEventInstructorsStatus = useAppSelector(
    selectAllEventInstructorsStatus,
  )
  let foamfratInstructors = useAppSelector(selectFOAMfratInstructors)
  let foamfratInstructorsStatus = useAppSelector(
    selectFOAMfratInstructorsStatus,
  )
  let instructorMap = useAppSelector(selectInstructors)

  let eventCompliance = eventCompliances[activeEvent || '']
  let eventData = events.find((event) => event.id === activeEvent)

  // Helper to check if a field should be visible based on filter
  let isFieldVisible = (status: ContentComplianceStatus | undefined) => {
    if (!status) return false
    if (detailsFilter === 'all') return true
    if (detailsFilter === 'pending') return status !== 'approved'
    if (detailsFilter === 'approved') return status === 'approved'
    return false
  }

  // Count visible fields per tab
  let tab0Visible = [
    isFieldVisible(eventCompliance?.name),
    isFieldVisible(eventCompliance?.description),
    isFieldVisible(eventCompliance?.hours),
    isFieldVisible(eventCompliance?.category),
    isFieldVisible(eventCompliance?.thumbnail),
    isFieldVisible(eventCompliance?.banner),
  ].filter(Boolean).length

  let tab1Visible =
    eventDetailsStatus === 'fulfilled'
      ? [
          isFieldVisible(eventCompliance?.capce),
          isFieldVisible(eventCompliance?.ancc),
          isFieldVisible(eventCompliance?.template),
          isFieldVisible(eventCompliance?.instructors),
        ].filter(Boolean).length
      : 0

  let tab2Visible =
    eventDetailsStatus === 'fulfilled'
      ? [
          isFieldVisible(eventCompliance?.practiceGap),
          isFieldVisible(eventCompliance?.practiceGapEvidence),
          isFieldVisible(eventCompliance?.learnerEngagement),
          isFieldVisible(eventCompliance?.learningOutcomes),
          isFieldVisible(eventCompliance?.objectives),
          isFieldVisible(eventCompliance?.references),
        ].filter(Boolean).length
      : 0

  let tab3Visible =
    eventDetailsStatus === 'fulfilled'
      ? isFieldVisible(eventCompliance?.quiz)
        ? 1
        : 0
      : 0

  // Helper function to calculate workflow status for an event
  let getEventWorkflowStatus = (
    event: (typeof events)[number],
  ): 'needs-revision' | 'in-planning' | 'ready-for-review' | 'approved' => {
    let progress = planningEventProgress[event.id]
    if (!progress) return 'in-planning'

    let dataComplete =
      (progress?.dataCompleteness?.percentComplete ?? 0) === 100
    let complianceComplete =
      (progress?.complianceStatus?.percentApproved ?? 0) === 100
    let hasRejected = (progress?.complianceStatus?.rejected ?? 0) > 0

    if (hasRejected) return 'needs-revision'
    if (!dataComplete) return 'in-planning'
    if (!complianceComplete) return 'ready-for-review'
    return 'approved'
  }

  // Helper function to calculate urgency score
  // 199% = most urgent, 0% = not urgent (not started), 200% = least urgent (complete)
  let getEventUrgencyScore = (event: (typeof events)[number]): number => {
    let progress = planningEventProgress[event.id]

    let dataProgress = progress?.dataCompleteness?.percentComplete ?? 0
    let complianceProgress = progress?.complianceStatus?.percentApproved ?? 0
    let rejectedCount = progress?.complianceStatus?.rejected ?? 0

    // If fully complete (both 100%), return 200% (least urgent)
    if (dataProgress === 100 && complianceProgress === 100) {
      return 200
    }

    // Base urgency is the average progress (0-100%)
    // More progress = more urgent (needs to be finished)
    let baseUrgency = (dataProgress + complianceProgress) / 2

    // Each rejection adds ~25% urgency
    let rejectionBoost = rejectedCount * 25

    // Total urgency: 0-199%, capped at 199%
    let urgency = Math.min(199, baseUrgency + rejectionBoost)

    return urgency
  }

  let lifecycleFilteredEvents = filterByLifecycle(events, lifecycleFilter)

  // Group events by workflow status
  let {
    needsRevisionEvents,
    inPlanningEvents,
    readyForReviewEvents,
    approvedEvents,
  } = groupBy((event: (typeof events)[number]) => {
    let status = getEventWorkflowStatus(event)
    return status === 'needs-revision'
      ? 'needsRevisionEvents'
      : status === 'in-planning'
        ? 'inPlanningEvents'
        : status === 'ready-for-review'
          ? 'readyForReviewEvents'
          : 'approvedEvents'
  })(lifecycleFilteredEvents)
  let baseFilteredEvents =
    eventFilter === 'needs-revision'
      ? needsRevisionEvents
      : eventFilter === 'in-planning'
        ? inPlanningEvents
        : eventFilter === 'ready-for-review'
          ? readyForReviewEvents
          : eventFilter === 'approved'
            ? approvedEvents
            : lifecycleFilteredEvents

  // Apply search filter
  let searchFilteredEvents = baseFilteredEvents?.filter((event) =>
    event.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  let instructorFilteredEvents = instructorFilter
    ? searchFilteredEvents?.filter((event) =>
        (instructorMap[event.id] ?? []).some((i) => i.id === instructorFilter),
      )
    : searchFilteredEvents

  let filteredEvents = instructorFilteredEvents
    ? sortBy((event) => {
        let urgency = getEventUrgencyScore(event)
        return urgency === 200 ? 1000 : -urgency
      }, instructorFilteredEvents)
    : []

  // Calculate counts for new workflow statuses
  let totalNeedsRevision = needsRevisionEvents?.length ?? 0
  let totalInPlanning = inPlanningEvents?.length ?? 0
  let totalReadyForReview = readyForReviewEvents?.length ?? 0
  let totalApproved = approvedEvents?.length ?? 0

  // Set default filter based on what needs attention
  useEffect(() => {
    if (
      planningEventProgressStatus === 'fulfilled' &&
      eventFilter === 'needs-revision'
    ) {
      // If no events need revision, switch to "ready-for-review" (most relevant for compliance)
      if (totalNeedsRevision === 0 && totalReadyForReview > 0) {
        setEventFilter('ready-for-review')
      } else if (totalNeedsRevision === 0 && totalInPlanning > 0) {
        setEventFilter('in-planning')
      }
    }
  }, [
    planningEventProgressStatus,
    totalNeedsRevision,
    totalReadyForReview,
    totalInPlanning,
  ])

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if (allEventInstructorsStatus === 'uninitialized') {
      dispatch(fetchAllEventInstructors())
    }
  }, [allEventInstructorsStatus])

  useEffect(() => {
    if (foamfratInstructorsStatus === 'uninitialized') {
      dispatch(fetchFOAMfratInstructors())
    }
  }, [foamfratInstructorsStatus])

  useEffect(() => {
    if (
      activeEvent &&
      (!eventDetailsStatus || eventDetailsStatus === 'uninitialized')
    ) {
      dispatch(fetchEventDetails(activeEvent))
    }
  }, [eventDetailsStatus, activeEvent])

  useEffect(() => {
    if (eventComplianceStatus === 'uninitialized') {
      dispatch(fetchEventCompliances())
    }
  }, [eventComplianceStatus])

  useEffect(() => {
    if (planningEventProgressStatus === 'uninitialized') {
      dispatch(fetchPlanningEventsProgress())
    }
  }, [planningEventProgressStatus])

  useEffect(() => {
    if (elementRef.current && window.innerWidth < 900) {
      let prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches
      ;(elementRef.current as any).scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      })
    }
  }, [activeEvent])

  useEffect(() => {
    if (!filteredEvents?.some((event) => event.id === activeEvent)) {
      setActiveEvent(undefined)
    }
  }, [eventFilter, searchQuery, lifecycleFilter, instructorFilter])

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
        Live Classes (F5) - Content Compliance Review Dashboard
      </Typography>
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{
          mb: 3,
        }}
      >
        Review and approve live class events for compliance with accreditation
        standards. Live classes are scheduled events where instructors teach in
        real-time. Verify event details, accreditation information, syllabus
        content, and assessments to ensure quality and regulatory compliance
        before classes go live.
      </Typography>
      <Card
        variant="outlined"
        sx={{ mb: 2, p: 3 }}
        component="aside"
        aria-label="Overview statistics"
      >
        <Typography variant="h6" component="h3">
          Overview
        </Typography>
        <Divider sx={{ my: 1 }} />
        {planningEventProgressStatus === 'fulfilled' ? (
          <Box
            sx={{ display: 'flex', columnGap: 4, rowGap: 1, flexWrap: 'wrap' }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.error.main,
                }}
              >
                {totalNeedsRevision}
              </Typography>
              <Typography variant="subtitle1">Needs Revision</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.contentPipelineColors.planning,
                }}
              >
                {totalInPlanning}
              </Typography>
              <Typography variant="subtitle1">In Planning</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.contentPipelineColors.compliance,
                }}
              >
                {totalReadyForReview}
              </Typography>
              <Typography variant="subtitle1">Ready for Review</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.success.main,
                }}
              >
                {totalApproved}
              </Typography>
              <Typography variant="subtitle1">Approved</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.contentPipelineColors.compliance,
                }}
              >
                {Object.values(planningEventProgress).reduce(
                  (sum, progress) =>
                    sum + (progress?.complianceStatus?.pending ?? 0),
                  0,
                )}
              </Typography>
              <Typography variant="subtitle1">Fields Pending</Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', columnGap: 4, rowGap: 1, flexWrap: 'wrap' }}
          >
            {[...Array(5)].map((_, i) => (
              <Box key={i} sx={{ minWidth: 120 }}>
                <Skeleton
                  variant="text"
                  sx={{
                    width: 60,
                    height: 32,
                    mb: 0.5,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: 120,
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Card>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 1.5 }}>
          Events Pending Review
        </Typography>
        <PipelineFilterBar
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search events…"
          status={eventFilter}
          onStatusChange={setEventFilter}
          statusCounts={{
            needsRevision: totalNeedsRevision,
            inPlanning: totalInPlanning,
            readyForReview: totalReadyForReview,
            approved: totalApproved,
            all: lifecycleFilteredEvents?.length ?? 0,
          }}
          lifecycle={lifecycleFilter}
          onLifecycleChange={setLifecycleFilter}
          lifecycleCounts={lifecycleCounts(events)}
          instructorFilter={instructorFilter}
          onInstructorChange={setInstructorFilter}
          instructors={foamfratInstructors}
        />
      </Paper>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { sm: '1fr', md: '350px 1fr' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            position: { md: 'sticky' },
            top: { md: 16 },
            maxHeight: { md: 'calc(100vh - 32px)' },
            overflowY: { md: 'auto' },
          }}
        >
          <Paper sx={{ p: 3 }} component="nav" aria-label="Events list">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filteredEvents?.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    bgcolor: 'background.paperDark',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    No Events Found
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {eventFilter === 'needs-revision'
                      ? 'Great! No events currently need revision.'
                      : eventFilter === 'in-planning'
                        ? 'No events are currently in planning.'
                        : eventFilter === 'ready-for-review'
                          ? 'No events are ready for compliance review.'
                          : eventFilter === 'approved'
                            ? 'No events have been fully approved yet.'
                            : searchQuery
                              ? `No events match "${searchQuery}".`
                              : 'No events available.'}
                  </Typography>
                </Box>
              ) : (
                filteredEvents?.map((event) => {
                  let checked = activeEvent === event.id
                  let progress = planningEventProgress[event.id]
                  let items = Object.entries(
                    eventCompliances[event.id] || {},
                  ).filter(
                    ([field, status]) =>
                      field !== 'eventID' && status !== 'approved',
                  ).length
                  let workflowStatus = getEventWorkflowStatus(event)

                  // Determine status badge color and label
                  let statusConfig = {
                    'needs-revision': {
                      label: 'Needs Revision',
                      color: 'error' as const,
                    },
                    'in-planning': {
                      label: 'In Planning',
                      color: 'primary' as const,
                    },
                    'ready-for-review': {
                      label: 'Ready for Review',
                      color: 'info' as const,
                    },
                    approved: {
                      label: 'Approved',
                      color: 'success' as const,
                    },
                  }[workflowStatus]

                  let dataProgress =
                    progress?.dataCompleteness?.percentComplete || 0
                  let complianceProgress =
                    progress?.complianceStatus?.percentApproved || 0

                  return (
                    <Card
                      key={event.id}
                      variant="outlined"
                      component="button"
                      sx={{
                        p: 2,
                        bgcolor: checked
                          ? 'rgba(25, 118, 210, 0.12)'
                          : 'background.paperDark',
                        '&:hover': {
                          cursor: 'pointer',
                          bgcolor: checked
                            ? 'rgba(25, 118, 210, 0.12)'
                            : 'divider',
                        },
                        '&:focus': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: '2px',
                        },
                        textAlign: 'left',
                        width: '100%',
                      }}
                      onClick={() => setActiveEvent(event.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setActiveEvent(event.id)
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-pressed={checked}
                      aria-label={`Select ${event.name} for compliance review - ${dataProgress}% data entry complete, ${complianceProgress}% compliance complete`}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'medium' }}
                        >
                          {event.name}
                        </Typography>
                        <LifecycleChip lifecycleState={event.lifecycleState} />
                      </Box>
                      <Box sx={{ mt: 1, mb: 1.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="caption">Data Entry</Typography>
                          <Typography variant="caption">
                            {dataProgress}%
                          </Typography>
                        </Box>
                        <Box
                          role="status"
                          aria-live="polite"
                          aria-atomic="true"
                        >
                          <LinearProgress
                            variant="determinate"
                            value={dataProgress}
                            aria-label={`Data entry progress: ${dataProgress}%`}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              mb: 1,
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor:
                                  theme.palette.contentPipelineColors.planning,
                              },
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="caption">Compliance</Typography>
                          <Typography variant="caption">
                            {complianceProgress}%
                          </Typography>
                        </Box>
                        <Box
                          role="status"
                          aria-live="polite"
                          aria-atomic="true"
                        >
                          <LinearProgress
                            variant="determinate"
                            value={complianceProgress}
                            aria-label={`Compliance progress: ${complianceProgress}%`}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor:
                                  theme.palette.contentPipelineColors
                                    .compliance,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography variant="subtitle2">
                          {event.hours || 0}h {'\u2022'} {event.categoryName}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          {(progress?.complianceStatus?.rejected ?? 0) > 0 && (
                            <Chip
                              color="error"
                              size="small"
                              label={`${progress.complianceStatus.rejected} rejected`}
                              variant="outlined"
                              aria-label={`${progress.complianceStatus.rejected} ${progress.complianceStatus.rejected === 1 ? 'field' : 'fields'} rejected and need revision`}
                            />
                          )}
                          {items > 0 && (
                            <Chip
                              size="small"
                              label={`${items} pending`}
                              aria-label={`${items} ${items === 1 ? 'field' : 'fields'} pending review`}
                              color={checked ? 'primary' : 'default'}
                            />
                          )}
                          <Chip
                            size="small"
                            label={statusConfig.label}
                            variant={checked ? 'filled' : 'outlined'}
                            aria-label={`Event status: ${statusConfig.label}`}
                            color={statusConfig.color}
                          />
                        </Box>
                      </Box>
                    </Card>
                  )
                })
              )}
            </Box>
          </Paper>
        </Box>
        <Box ref={elementRef} component="main" role="main">
          {!activeEvent ? (
            <SelectItemHint type="event" />
          ) : (
            <>
              <Card variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="h5" component="h2">
                      {eventData
                        ? `Compliance Review: ${eventData.name}`
                        : 'Compliance Review'}
                    </Typography>
                    <LifecycleChip lifecycleState={eventData?.lifecycleState} />
                  </Box>
                  {canTransitionLifecycle && eventData && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {eventData.lifecycleState === 'active' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setLifecycleConfirm('lifecycle-pre-archive')
                          }
                        >
                          Move to pre-archive
                        </Button>
                      )}
                      {eventData.lifecycleState === 'preArchive' && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setLifecycleConfirm('lifecycle-reactivate')
                            }
                          >
                            Reactivate
                          </Button>
                          <Button
                            color="warning"
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setLifecycleConfirm('lifecycle-archive')
                            }
                          >
                            Archive
                          </Button>
                        </>
                      )}
                      {eventData.lifecycleState === 'archive' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setLifecycleConfirm('lifecycle-restore')
                          }
                        >
                          Restore
                        </Button>
                      )}
                      <Button
                        color="error"
                        size="small"
                        variant="outlined"
                        onClick={() => setLifecycleConfirm('delete-event')}
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                </Box>
                {activeEvent && eventData && (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'medium' }}
                        >
                          Data Entry
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          {planningEventProgress[activeEvent]?.dataCompleteness
                            ?.percentComplete ?? 0}
                          %
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={
                          planningEventProgress[activeEvent]?.dataCompleteness
                            ?.percentComplete ?? 0
                        }
                        sx={{
                          height: 6,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor:
                              theme.palette.contentPipelineColors.planning,
                          },
                        }}
                      />
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'medium' }}
                        >
                          Compliance
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          {planningEventProgress[activeEvent]?.complianceStatus
                            ?.percentApproved ?? 0}
                          %
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={
                          planningEventProgress[activeEvent]?.complianceStatus
                            ?.percentApproved ?? 0
                        }
                        sx={{
                          height: 6,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor:
                              theme.palette.contentPipelineColors.compliance,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Card>
              {activeEvent && eventData && (
                <>
                  <ToggleButtonGroup
                    color="primary"
                    sx={{
                      mb: 3,
                      bgcolor: 'background.paperDark',
                      borderRadius: 1,
                      p: 0.5,
                      '& .MuiToggleButton-root': {
                        border: 0,
                        borderRadius: 1,
                        textTransform: 'none',
                        px: 2,
                      },
                      '& .Mui-selected': {
                        bgcolor: 'action.selected',
                      },
                    }}
                    size="small"
                    value={detailsFilter}
                    exclusive
                    onChange={(_, value) => value && setDetailsFilter(value)}
                    aria-label="Filter fields by compliance status"
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="pending">Pending</ToggleButton>
                    <ToggleButton value="approved">Approved</ToggleButton>
                  </ToggleButtonGroup>
                  <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    variant="scrollable"
                    scrollButtons
                    allowScrollButtonsMobile
                    sx={{
                      mb: 3,
                      borderBottom: 1,
                      borderColor: 'divider',
                      minHeight: 40,
                      '& .MuiTab-root': {
                        minHeight: 40,
                        textTransform: 'none',
                      },
                    }}
                    aria-label="Event compliance sections"
                  >
                    <Tab
                      label="Event Details"
                      id="tab-0"
                      aria-controls="tabpanel-0"
                    />
                    <Tab
                      label="Accreditation & Setup"
                      id="tab-1"
                      aria-controls="tabpanel-1"
                    />
                    <Tab
                      label="Syllabus"
                      id="tab-2"
                      aria-controls="tabpanel-2"
                    />
                    <Tab
                      label="Assessment"
                      id="tab-3"
                      aria-controls="tabpanel-3"
                    />
                  </Tabs>
                </>
              )}
              {activeEvent && eventData && activeTab === 0 && (
                <Box role="tabpanel" id="tabpanel-0" aria-labelledby="tab-0">
                  {tab0Visible === 0 ? (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'background.paperDark',
                        borderRadius: 1,
                      }}
                    >
                      <Typography color="text.secondary">
                        No{' '}
                        {detailsFilter === 'approved' ? 'approved' : 'pending'}{' '}
                        fields to display in this tab.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      <FieldApproval
                        eventID={activeEvent}
                        name="name"
                        children={<Typography>{eventData?.name}</Typography>}
                        status={eventCompliance?.name}
                        filter={detailsFilter}
                        onEdit={() => {
                          setEditModalField({
                            type: 'event',
                            fieldName: 'name',
                            fieldLabel: 'Name',
                          })
                          setEditModalOpen(true)
                        }}
                      />
                      <FieldApproval
                        eventID={activeEvent}
                        name="description"
                        children={
                          <Typography>
                            {eventDetails?.syllabus?.text ||
                              eventData?.description}
                          </Typography>
                        }
                        status={eventCompliance?.description}
                        filter={detailsFilter}
                        onEdit={() => {
                          setEditModalField({
                            type: 'event',
                            fieldName: 'description',
                            fieldLabel: 'Description',
                          })
                          setEditModalOpen(true)
                        }}
                      />
                      <FieldApproval
                        eventID={activeEvent}
                        name="hours"
                        children={
                          <Typography>
                            {eventData?.hours || 0} Hour
                            {eventData?.hours === 1 ? '' : 's'}
                          </Typography>
                        }
                        status={eventCompliance?.hours}
                        filter={detailsFilter}
                        onEdit={() => {
                          setEditModalField({
                            type: 'event',
                            fieldName: 'hours',
                            fieldLabel: 'Length (Hours)',
                          })
                          setEditModalOpen(true)
                        }}
                      />
                      <FieldApproval
                        eventID={activeEvent}
                        name="category"
                        children={
                          <Typography>{eventData?.categoryName}</Typography>
                        }
                        status={eventCompliance?.category}
                        filter={detailsFilter}
                        onEdit={() => {
                          setEditModalField({
                            type: 'event',
                            fieldName: 'category',
                            fieldLabel: 'Category',
                          })
                          setEditModalOpen(true)
                        }}
                      />
                      <FieldApproval
                        eventID={activeEvent}
                        name="thumbnail"
                        children={
                          <img
                            style={{
                              maxHeight: '180px',
                              aspectRatio: '16 / 9',
                              borderRadius: '4px',
                            }}
                            src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${eventData?.thumbnail?.replace('originals', 'resized')}`}
                            alt={`Thumbnail image for ${eventData?.name}`}
                          />
                        }
                        status={eventCompliance?.thumbnail}
                        filter={detailsFilter}
                        onEdit={() => {
                          setEditModalField({
                            type: 'event',
                            fieldName: 'thumbnail',
                            fieldLabel: 'Thumbnail Image',
                          })
                          setEditModalOpen(true)
                        }}
                      />
                      <FieldApproval
                        eventID={activeEvent}
                        name="banner"
                        children={
                          <img
                            style={{
                              maxHeight: '180px',
                              aspectRatio: '4 / 1',
                              borderRadius: '4px',
                            }}
                            src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${eventData?.banner?.replace('originals', 'resized')}`}
                            alt={`Banner image for ${eventData?.name}`}
                          />
                        }
                        status={eventCompliance?.banner}
                        filter={detailsFilter}
                        onEdit={() => {
                          setEditModalField({
                            type: 'event',
                            fieldName: 'banner',
                            fieldLabel: 'Banner Image',
                          })
                          setEditModalOpen(true)
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 1: Accreditation & Setup */}
              {activeEvent && eventData && activeTab === 1 && (
                <Box role="tabpanel" id="tabpanel-1" aria-labelledby="tab-1">
                  {tab1Visible === 0 ? (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'background.paperDark',
                        borderRadius: 1,
                      }}
                    >
                      <Typography color="text.secondary">
                        No{' '}
                        {detailsFilter === 'approved' ? 'approved' : 'pending'}{' '}
                        fields to display in this tab.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      {eventDetailsStatus === 'fulfilled' ? (
                        <>
                          <FieldApproval
                            eventID={activeEvent}
                            name="capce"
                            children={
                              eventDetails.capceAccreditation ? (
                                <CAPCEApproval
                                  accreditation={
                                    eventDetails.capceAccreditation
                                  }
                                />
                              ) : (
                                <Typography>None</Typography>
                              )
                            }
                            status={eventCompliance?.capce}
                            filter={detailsFilter}
                            onEdit={
                              eventDetails.capceAccreditation
                                ? () => {
                                    setEditModalField({
                                      type: 'capce-full',
                                      fieldLabel: 'CAPCE Accreditation',
                                    })
                                    setEditModalOpen(true)
                                  }
                                : undefined
                            }
                          />
                          <FieldApproval
                            eventID={activeEvent}
                            name="ancc"
                            children={
                              eventDetails.anccAccreditation ? (
                                <ANCCApproval
                                  accreditation={eventDetails.anccAccreditation}
                                />
                              ) : (
                                <Typography>None</Typography>
                              )
                            }
                            status={eventCompliance?.ancc}
                            filter={detailsFilter}
                            onEdit={
                              eventDetails.anccAccreditation
                                ? () => {
                                    setEditModalField({
                                      type: 'ancc-full',
                                      fieldLabel: 'ANCC Accreditation',
                                    })
                                    setEditModalOpen(true)
                                  }
                                : undefined
                            }
                          />
                          <FieldApproval
                            eventID={activeEvent}
                            name="template"
                            children={
                              <Typography>
                                {eventDetails.template?.name || 'None'}
                              </Typography>
                            }
                            status={eventCompliance?.template}
                            filter={detailsFilter}
                            onEdit={() => {
                              setEditModalField({
                                type: 'template',
                                fieldLabel: 'Certificate Template',
                              })
                              setEditModalOpen(true)
                            }}
                          />
                          <FieldApproval
                            eventID={activeEvent}
                            name="instructors"
                            children={
                              !eventDetails.instructors?.length ? (
                                <Typography>None</Typography>
                              ) : (
                                <Box>
                                  {eventDetails.instructors?.map((ins) => (
                                    <Typography key={ins.id}>
                                      {'\u2022'} {ins.name}
                                    </Typography>
                                  ))}
                                </Box>
                              )
                            }
                            status={eventCompliance?.instructors}
                            filter={detailsFilter}
                            onEdit={() => {
                              setEditModalField({
                                type: 'instructors',
                                fieldLabel: 'Instructors',
                              })
                              setEditModalOpen(true)
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <Skeleton
                            variant="rounded"
                            sx={{
                              height: 180,
                            }}
                          />
                          <Skeleton
                            variant="rounded"
                            sx={{
                              height: 180,
                            }}
                          />
                          <Skeleton
                            variant="rounded"
                            sx={{
                              height: 180,
                            }}
                          />
                          <Skeleton
                            variant="rounded"
                            sx={{
                              height: 180,
                            }}
                          />
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 2: Syllabus */}
              {activeEvent && eventData && activeTab === 2 && (
                <Box role="tabpanel" id="tabpanel-2" aria-labelledby="tab-2">
                  {tab2Visible === 0 ? (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'background.paperDark',
                        borderRadius: 1,
                      }}
                    >
                      <Typography color="text.secondary">
                        No{' '}
                        {detailsFilter === 'approved' ? 'approved' : 'pending'}{' '}
                        fields to display in this tab.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      {eventDetailsStatus === 'fulfilled' ? (
                        <>
                          <FieldApproval
                            eventID={activeEvent}
                            name="practiceGap"
                            children={
                              <Typography>
                                {eventDetails.syllabus?.practiceGap || 'None'}
                              </Typography>
                            }
                            status={eventCompliance?.practiceGap}
                            filter={detailsFilter}
                            onEdit={() => {
                              setEditModalField({
                                type: 'syllabus',
                                fieldName: 'practiceGap',
                                fieldLabel: 'Practice Gap',
                              })
                              setEditModalOpen(true)
                            }}
                          />
                          <FieldApproval
                            eventID={activeEvent}
                            name="practiceGapEvidence"
                            children={
                              <Typography>
                                {eventDetails.syllabus?.practiceGapEvidence ||
                                  'None'}
                              </Typography>
                            }
                            status={eventCompliance?.practiceGapEvidence}
                            filter={detailsFilter}
                            onEdit={() => {
                              setEditModalField({
                                type: 'syllabus',
                                fieldName: 'practiceGapEvidence',
                                fieldLabel: 'Practice Gap Evidence',
                              })
                              setEditModalOpen(true)
                            }}
                          />
                          <FieldApproval
                            eventID={activeEvent}
                            name="learnerEngagement"
                            children={
                              <Typography>
                                {eventDetails.syllabus?.learnerEngagement ||
                                  'None'}
                              </Typography>
                            }
                            status={eventCompliance?.learnerEngagement}
                            filter={detailsFilter}
                            onEdit={() => {
                              setEditModalField({
                                type: 'syllabus',
                                fieldName: 'learnerEngagement',
                                fieldLabel: 'Learner Engagement',
                              })
                              setEditModalOpen(true)
                            }}
                          />
                          <FieldApproval
                            eventID={activeEvent}
                            name="learningOutcomes"
                            children={
                              <Typography>
                                {eventDetails.syllabus?.learningOutcomes ||
                                  'None'}
                              </Typography>
                            }
                            status={eventCompliance?.learningOutcomes}
                            filter={detailsFilter}
                            onEdit={() => {
                              setEditModalField({
                                type: 'syllabus',
                                fieldName: 'learningOutcomes',
                                fieldLabel: 'Learning Outcomes',
                              })
                              setEditModalOpen(true)
                            }}
                          />
                          <FieldApproval
                            eventID={activeEvent}
                            name="objectives"
                            children={
                              !eventDetails.syllabus?.objectives?.length ? (
                                <Typography>None</Typography>
                              ) : (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                  }}
                                >
                                  {eventDetails.syllabus.objectives?.map(
                                    (obj, index) => (
                                      <Paper
                                        key={obj.id}
                                        variant="outlined"
                                        sx={{ p: 2 }}
                                      >
                                        <Typography
                                          color="text.secondary"
                                          variant="body2"
                                          sx={{
                                            mb: 0.5,
                                          }}
                                        >
                                          Objective {index + 1}
                                        </Typography>
                                        <Typography variant="body1">
                                          {obj.text}
                                        </Typography>
                                      </Paper>
                                    ),
                                  )}
                                </Box>
                              )
                            }
                            status={eventCompliance?.objectives}
                            filter={detailsFilter}
                          />
                          <FieldApproval
                            eventID={activeEvent}
                            name="references"
                            children={
                              !eventDetails.syllabus?.references?.length ? (
                                <Typography>None</Typography>
                              ) : (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                  }}
                                >
                                  {eventDetails.syllabus.references?.map(
                                    (ref, index) => (
                                      <Paper
                                        key={ref.id}
                                        variant="outlined"
                                        sx={{ p: 2 }}
                                      >
                                        <Typography
                                          color="text.secondary"
                                          variant="body2"
                                          sx={{ mb: 0.5 }}
                                        >
                                          Reference {index + 1}
                                        </Typography>
                                        <Typography variant="body1">
                                          {ref.text}
                                        </Typography>
                                      </Paper>
                                    ),
                                  )}
                                </Box>
                              )
                            }
                            status={eventCompliance?.references}
                            filter={detailsFilter}
                          />
                        </>
                      ) : (
                        <>
                          <Skeleton
                            variant="rounded"
                            sx={{
                              height: 180,
                            }}
                          />
                          <Skeleton
                            variant="rounded"
                            sx={{
                              height: 180,
                            }}
                          />
                          <Skeleton
                            variant="rounded"
                            sx={{
                              height: 180,
                            }}
                          />
                          <Skeleton
                            variant="rounded"
                            sx={{
                              height: 180,
                            }}
                          />
                          <Skeleton
                            variant="rounded"
                            sx={{
                              height: 180,
                            }}
                          />
                          <Skeleton
                            variant="rounded"
                            sx={{
                              height: 180,
                            }}
                          />
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 3: Assessment */}
              {activeEvent && eventData && activeTab === 3 && (
                <Box role="tabpanel" id="tabpanel-3" aria-labelledby="tab-3">
                  {tab3Visible === 0 ? (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'background.paperDark',
                        borderRadius: 1,
                      }}
                    >
                      <Typography color="text.secondary">
                        No{' '}
                        {detailsFilter === 'approved' ? 'approved' : 'pending'}{' '}
                        fields to display in this tab.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      {eventDetailsStatus === 'fulfilled' ? (
                        <FieldApproval
                          eventID={activeEvent}
                          name="quiz"
                          children={
                            eventDetails.quiz ? (
                              <QuizApproval
                                quiz={eventDetails.quiz}
                                password={
                                  eventData?.attendanceTrackingMechanism
                                    ?.kind === 'quizPasscode'
                                    ? eventData.attendanceTrackingMechanism
                                        .quizPasscode
                                    : ''
                                }
                              />
                            ) : (
                              <Typography>None</Typography>
                            )
                          }
                          status={eventCompliance?.quiz}
                          filter={detailsFilter}
                        />
                      ) : (
                        <Skeleton
                          variant="rounded"
                          sx={{
                            height: 180,
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={lifecycleOperationStatus === 'fulfilled'}
        autoHideDuration={4000}
        onClose={() => dispatch(clearEventLifecycleOperationStatus())}
      >
        <Alert
          variant="filled"
          severity="success"
          onClose={() => dispatch(clearEventLifecycleOperationStatus())}
        >
          {lifecycleOperationSuccess}
        </Alert>
      </Snackbar>
      <Snackbar
        open={lifecycleOperationStatus === 'rejected'}
        autoHideDuration={6000}
        onClose={() => dispatch(clearEventLifecycleOperationStatus())}
      >
        <Alert
          variant="filled"
          severity="error"
          onClose={() => dispatch(clearEventLifecycleOperationStatus())}
        >
          {lifecycleOperationError}
        </Alert>
      </Snackbar>
      {activeEvent && (
        <>
          <ConfirmAction
            open={lifecycleConfirm}
            setOpen={setLifecycleConfirm}
            openText="lifecycle-pre-archive"
            title="Move event to pre-archive?"
            text="The event will be visually distinguished on dashboards but still visible. An email notification will be sent."
            confirmText="Move to pre-archive"
            executeAction={async () => {
              // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
              await dispatch(preArchiveEvent(activeEvent))
            }}
          />
          <ConfirmAction
            open={lifecycleConfirm}
            setOpen={setLifecycleConfirm}
            openText="lifecycle-reactivate"
            title="Reactivate event?"
            text="The event will return to the active dashboard."
            confirmText="Reactivate"
            executeAction={async () => {
              // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
              await dispatch(reactivateEvent(activeEvent))
            }}
          />
          <ConfirmAction
            open={lifecycleConfirm}
            setOpen={setLifecycleConfirm}
            openText="lifecycle-archive"
            title="Archive event?"
            text="Archiving will flip CAPCE compliance to Unapproved and hide the event from default views. An email notification will be sent."
            confirmText="Archive"
            executeAction={async () => {
              // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
              await dispatch(archiveEvent(activeEvent))
            }}
          />
          <ConfirmAction
            open={lifecycleConfirm}
            setOpen={setLifecycleConfirm}
            openText="lifecycle-restore"
            title="Restore event?"
            text="The event will return to pre-archive. CAPCE compliance will remain Unapproved until the event is re-reviewed."
            confirmText="Restore"
            executeAction={async () => {
              // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
              await dispatch(restoreEvent(activeEvent))
            }}
          />
          <ConfirmAction
            open={lifecycleConfirm}
            setOpen={setLifecycleConfirm}
            openText="delete-event"
            title="Permanently delete event?"
            text={`This will permanently delete "${eventData?.name}" and all associated compliance data. This action cannot be undone. Use Archive for classes that have served their purpose — Delete is only for accidental duplicates that should never have existed.`}
            confirmText="Delete permanently"
            executeAction={async () => {
              // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
              await dispatch(deletePlanningEvent(activeEvent))
              setActiveEvent(undefined)
            }}
          />
        </>
      )}
      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false)
          setEditModalField(null)
        }}
        fullWidth
        aria-labelledby="edit-modal-title"
        aria-describedby="edit-modal-description"
        slotProps={{
          paper: modalPaperProps,
        }}
        maxWidth="md"
      >
        <DialogTitle id="edit-modal-title">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              Edit {editModalField?.fieldLabel}
            </Typography>
            <IconButton
              onClick={() => {
                setEditModalOpen(false)
                setEditModalField(null)
              }}
              size="small"
              aria-label="Close edit dialog"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent id="edit-modal-description">
          {editModalField && activeEvent && eventData && (
            <>
              {editModalField.type === 'event' && (
                <EventField
                  eventID={activeEvent}
                  event={eventData}
                  fieldName={editModalField.fieldName as any}
                  fieldLabel={editModalField.fieldLabel}
                  fieldType={
                    editModalField.fieldName === 'description'
                      ? 'textarea'
                      : editModalField.fieldName === 'hours'
                        ? 'number'
                        : editModalField.fieldName === 'category'
                          ? 'select'
                          : editModalField.fieldName === 'thumbnail' ||
                              editModalField.fieldName === 'banner'
                            ? 'image'
                            : 'text'
                  }
                  complianceStatus={
                    eventCompliance?.[
                      editModalField.fieldName as keyof EventCompliance
                    ] as ContentComplianceStatus
                  }
                  viewMode="modal"
                  onClose={() => {
                    setEditModalOpen(false)
                    setEditModalField(null)
                    // Refetch data after edit
                    dispatch(fetchEventDetails(activeEvent))
                    dispatch(fetchEvents())
                  }}
                />
              )}
              {editModalField.type === 'syllabus' && eventDetails?.syllabus && (
                <SyllabusFieldEditable
                  eventID={activeEvent}
                  syllabus={eventDetails.syllabus}
                  fieldName={editModalField.fieldName as any}
                  fieldLabel={editModalField.fieldLabel}
                  fieldType="textarea"
                  complianceStatus={
                    eventCompliance?.[
                      editModalField.fieldName as keyof EventCompliance
                    ] as ContentComplianceStatus
                  }
                  viewMode="modal"
                  onClose={() => {
                    setEditModalOpen(false)
                    setEditModalField(null)
                    // Refetch data after edit
                    dispatch(fetchEventDetails(activeEvent))
                  }}
                />
              )}
              {editModalField.type === 'capce-full' &&
                eventDetails?.capceAccreditation && (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                  >
                    <CAPCEFieldEditable
                      eventID={activeEvent}
                      accreditation={eventDetails.capceAccreditation}
                      fieldName="courseCode"
                      fieldLabel="Course Code"
                      fieldType="text"
                      required
                      viewMode="modal"
                      onClose={() => {
                        // Refetch event details to get updated CAPCE data
                        dispatch(fetchEventDetails(activeEvent))
                      }}
                    />

                    <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <CAPCEFieldEditable
                        eventID={activeEvent}
                        accreditation={eventDetails.capceAccreditation}
                        fieldName="topicArea"
                        fieldLabel="Topic Area"
                        fieldType="select"
                        required
                        viewMode="modal"
                        onClose={() => {
                          dispatch(fetchEventDetails(activeEvent))
                        }}
                      />

                      <CAPCEFieldEditable
                        eventID={activeEvent}
                        accreditation={eventDetails.capceAccreditation}
                        fieldName="classType"
                        fieldLabel="Class Type"
                        fieldType="select"
                        required
                        viewMode="modal"
                        onClose={() => {
                          dispatch(fetchEventDetails(activeEvent))
                        }}
                      />
                    </Box>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 'medium',
                        mt: 1,
                      }}
                    >
                      Important Dates
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(3, 1fr)',
                        },
                        gap: 3,
                      }}
                    >
                      <CAPCEFieldEditable
                        eventID={activeEvent}
                        accreditation={eventDetails.capceAccreditation}
                        fieldName="applicationDate"
                        fieldLabel="Application Date"
                        fieldType="date"
                        required
                        viewMode="modal"
                        onClose={() => {
                          dispatch(fetchEventDetails(activeEvent))
                        }}
                      />

                      <CAPCEFieldEditable
                        eventID={activeEvent}
                        accreditation={eventDetails.capceAccreditation}
                        fieldName="submissionDate"
                        fieldLabel="Submission Date"
                        fieldType="date"
                        viewMode="modal"
                        onClose={() => {
                          dispatch(fetchEventDetails(activeEvent))
                        }}
                      />

                      <CAPCEFieldEditable
                        eventID={activeEvent}
                        accreditation={eventDetails.capceAccreditation}
                        fieldName="expirationDate"
                        fieldLabel="Expiration Date"
                        fieldType="date"
                        required
                        viewMode="modal"
                        onClose={() => {
                          dispatch(fetchEventDetails(activeEvent))
                        }}
                      />
                    </Box>

                    <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <CAPCEFieldEditable
                        eventID={activeEvent}
                        accreditation={eventDetails.capceAccreditation}
                        fieldName="allClearDate"
                        fieldLabel="All Clear Date"
                        fieldType="date"
                        viewMode="modal"
                        onClose={() => {
                          dispatch(fetchEventDetails(activeEvent))
                        }}
                      />

                      <CAPCEFieldEditable
                        eventID={activeEvent}
                        accreditation={eventDetails.capceAccreditation}
                        fieldName="goLiveDate"
                        fieldLabel="Go Live Date"
                        fieldType="date"
                        viewMode="modal"
                        onClose={() => {
                          dispatch(fetchEventDetails(activeEvent))
                        }}
                      />
                    </Box>
                  </Box>
                )}
              {editModalField.type === 'ancc-full' &&
                eventDetails?.anccAccreditation && (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                  >
                    <ANCCFieldEditable
                      eventID={activeEvent}
                      accreditation={eventDetails.anccAccreditation}
                      fieldName="courseCode"
                      fieldLabel="Course Code"
                      fieldType="text"
                      required
                      viewMode="modal"
                      onClose={() => {
                        dispatch(fetchEventDetails(activeEvent))
                      }}
                    />

                    <ANCCFieldEditable
                      eventID={activeEvent}
                      accreditation={eventDetails.anccAccreditation}
                      fieldName="expirationDate"
                      fieldLabel="Expiration Date"
                      fieldType="date"
                      required
                      viewMode="modal"
                      onClose={() => {
                        dispatch(fetchEventDetails(activeEvent))
                      }}
                    />

                    <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <ANCCFieldEditable
                        eventID={activeEvent}
                        accreditation={eventDetails.anccAccreditation}
                        fieldName="targetAudience"
                        fieldLabel="Target Audience"
                        fieldType="multiselect"
                        required
                        viewMode="modal"
                        onClose={() => {
                          dispatch(fetchEventDetails(activeEvent))
                        }}
                      />

                      <ANCCFieldEditable
                        eventID={activeEvent}
                        accreditation={eventDetails.anccAccreditation}
                        fieldName="educationalNeeds"
                        fieldLabel="Educational Needs"
                        fieldType="multiselect"
                        required
                        viewMode="modal"
                        onClose={() => {
                          dispatch(fetchEventDetails(activeEvent))
                        }}
                      />
                    </Box>

                    <ANCCFieldEditable
                      eventID={activeEvent}
                      accreditation={eventDetails.anccAccreditation}
                      fieldName="competency"
                      fieldLabel="Competency"
                      fieldType="textarea"
                      viewMode="modal"
                      onClose={() => {
                        dispatch(fetchEventDetails(activeEvent))
                      }}
                    />

                    <ANCCFieldEditable
                      eventID={activeEvent}
                      accreditation={eventDetails.anccAccreditation}
                      fieldName="professionalSource"
                      fieldLabel="Professional Source"
                      fieldType="textarea"
                      viewMode="modal"
                      onClose={() => {
                        dispatch(fetchEventDetails(activeEvent))
                      }}
                    />
                  </Box>
                )}
              {editModalField.type === 'template' && (
                <TemplateManagement
                  eventID={activeEvent}
                  template={eventDetails?.template}
                  complianceStatus={eventCompliance?.template}
                  onUpdate={() => {
                    dispatch(fetchEventDetails(activeEvent))
                  }}
                />
              )}
              {editModalField.type === 'instructors' && (
                <InstructorManagement
                  eventID={activeEvent}
                  instructors={eventDetails?.instructors}
                  complianceStatus={eventCompliance?.instructors}
                  onUpdate={() => {
                    dispatch(fetchEventDetails(activeEvent))
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}
